import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, AlertTriangle, Mail, Lock, Trash2, LogOut } from "lucide-react";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BrutalSelect, BrutalSelectContent, BrutalSelectItem, BrutalSelectTrigger, BrutalSelectValue } from "@/components/ui/BrutalSelect";
import i18n from "@/i18n/config";

interface Preferences {
  email_newsletter: boolean;
  email_product_updates: boolean;
  email_event_invites: boolean;
  email_review_notifications: boolean;
  email_affiliate_updates: boolean;
  email_marketing: boolean;
  notify_new_reviews: boolean;
  notify_event_reminders: boolean;
  notify_subscription_changes: boolean;
  notify_payout_available: boolean;
  notify_profile_views: boolean;
  digest_frequency: string;
  preferred_time: string;
  timezone: string;
  preferred_language: string | null;
}

const defaultPreferences: Preferences = {
  email_newsletter: true,
  email_product_updates: true,
  email_event_invites: true,
  email_review_notifications: true,
  email_affiliate_updates: true,
  email_marketing: true,
  notify_new_reviews: true,
  notify_event_reminders: true,
  notify_subscription_changes: true,
  notify_payout_available: true,
  notify_profile_views: false,
  digest_frequency: "weekly",
  preferred_time: "09:00",
  timezone: "Europe/Rome",
  preferred_language: null,
};

interface PreferenceToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function PreferenceToggle({ label, description, checked, onChange }: PreferenceToggleProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b last:border-0">
      <div className="flex-1">
        <Label className="font-bold">{label}</Label>
        {description && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export default function Preferences() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState<Preferences>(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("communications");
  
  // Security tab states
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  
  // Danger zone states
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      loadPreferences();
      checkActiveSubscription();
    }
  }, [user]);

  const checkActiveSubscription = async () => {
    try {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("id, status")
        .eq("user_id", user?.id)
        .in("status", ["active", "past_due"])
        .maybeSingle();

      if (error && error.code !== "PGRST116") throw error;
      setHasActiveSubscription(!!data);
    } catch (error) {
      console.error("Error checking subscription:", error);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", user?.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        setPreferences({
          email_newsletter: data.email_newsletter,
          email_product_updates: data.email_product_updates,
          email_event_invites: data.email_event_invites,
          email_review_notifications: data.email_review_notifications,
          email_affiliate_updates: data.email_affiliate_updates,
          email_marketing: data.email_marketing,
          notify_new_reviews: data.notify_new_reviews,
          notify_event_reminders: data.notify_event_reminders,
          notify_subscription_changes: data.notify_subscription_changes,
          notify_payout_available: data.notify_payout_available,
          notify_profile_views: data.notify_profile_views,
          digest_frequency: data.digest_frequency,
          preferred_time: data.preferred_time,
          timezone: data.timezone,
          preferred_language: data.preferred_language,
        });
      }
    } catch (error) {
      console.error("Error loading preferences:", error);
      toast.error(t("preferences.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("user_preferences")
        .upsert({
          user_id: user?.id,
          email_newsletter: preferences.email_newsletter,
          email_product_updates: preferences.email_product_updates,
          email_event_invites: preferences.email_event_invites,
          email_review_notifications: preferences.email_review_notifications,
          email_affiliate_updates: preferences.email_affiliate_updates,
          email_marketing: preferences.email_marketing,
          notify_new_reviews: preferences.notify_new_reviews,
          notify_event_reminders: preferences.notify_event_reminders,
          notify_subscription_changes: preferences.notify_subscription_changes,
          notify_payout_available: preferences.notify_payout_available,
          notify_profile_views: preferences.notify_profile_views,
          digest_frequency: preferences.digest_frequency,
          preferred_time: preferences.preferred_time,
          timezone: preferences.timezone,
          preferred_language: preferences.preferred_language,
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      setSaved(true);
      toast.success(t("preferences.saved"));
      setTimeout(() => setSaved(false), 3000);
    } catch (error: any) {
      console.error("Error saving preferences:", error);
      toast.error(error.message || t("preferences.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail || !emailPassword) {
      toast.error(t("preferences.security.passwordRequired"));
      return;
    }

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email!,
        password: emailPassword
      });

      if (signInError) {
        toast.error(t("auth.invalidCredentials"));
        return;
      }

      const { error } = await supabase.auth.updateUser({ email: newEmail });
      if (error) throw error;

      toast.success(t("preferences.security.emailVerificationSent"));
      setShowEmailDialog(false);
      setNewEmail("");
      setEmailPassword("");
    } catch (error: any) {
      console.error("Error changing email:", error);
      toast.error(error.message);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t("preferences.security.passwordRequired"));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t("auth.passwordsDontMatch"));
      return;
    }

    if (newPassword.length < 8) {
      toast.error(t("preferences.security.passwordRequirements"));
      return;
    }

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email!,
        password: currentPassword
      });

      if (signInError) {
        toast.error(t("auth.invalidCredentials") + " - La password attuale non è corretta");
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      
      if (error) {
        throw error;
      }

      toast.success(t("preferences.security.passwordChangeSuccess") + " - Ora puoi accedere con la nuova password");
      setShowPasswordDialog(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast.error("Errore cambio password: " + (error.message || "Errore sconosciuto"));
    }
  };

  const handleSignOutAll = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
      toast.success(t("preferences.security.signOutAllSuccess"));
      navigate("/");
    } catch (error: any) {
      console.error("Error signing out:", error);
      toast.error(error.message);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword || !deleteConfirmed) {
      toast.error(t("preferences.dangerZone.passwordRequired"));
      return;
    }

    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account", {
        body: { password: deletePassword }
      });

      if (error) throw error;

      toast.success(t("preferences.dangerZone.deleteSuccess"));
      await supabase.auth.signOut();
      navigate("/");
    } catch (error: any) {
      console.error("Error deleting account:", error);
      toast.error(error.message || t("preferences.dangerZone.mustCancelFirst"));
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <>
        <SEO 
          title={t("preferences.pageTitle")}
          description={t("preferences.pageDescription")}
        />
        <Header />
        <div className="min-h-screen bg-surface">
          <div className="container mx-auto px-4 py-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-surface rounded w-1/4"></div>
              <div className="h-64 bg-surface rounded"></div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO 
        title={t("preferences.pageTitle")}
        description={t("preferences.pageDescription")}
      />
      <Header />
      <div className="min-h-screen bg-surface">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-4 text-sm">
            <Link to="/dashboard" className="text-muted-foreground hover:text-primary transition-colors">
              Dashboard
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground font-semibold">
              {t("preferences.title")}
            </span>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-accent transition-colors rounded-lg"
              aria-label={t("common.back")}
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-heading text-3xl font-bold">
              {t("preferences.title")}
            </h1>
          </div>

          {saved && (
            <div className="mb-4 p-3 bg-success/10 border-[2px] border-success text-success">
              ✓ {t("preferences.savedMessage")}
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="communications" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {t("preferences.tabs.communications")}
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                {t("preferences.tabs.security")}
              </TabsTrigger>
              <TabsTrigger value="danger" className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-4 h-4" />
                {t("preferences.tabs.danger")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="communications" className="space-y-6">
              <BrutalCard className="mb-6">
        <h2 className="font-heading text-xl font-bold mb-4">
          {t("preferences.emailNotifications")}
        </h2>

        {/* Language Selection */}
        <div className="mb-6 pb-6 border-b-[3px] border-dark">
          <Label className="mb-2 block font-bold">
            Lingua Email
          </Label>
          <p className="text-sm text-muted-foreground mb-3">
            Scegli la lingua per le comunicazioni email. Se selezioni "Automatica", 
            useremo la lingua dell'interfaccia attualmente impostata.
          </p>
          <BrutalSelect
            value={preferences.preferred_language || "auto"}
            onValueChange={(value) => 
              setPreferences({ 
                ...preferences, 
                preferred_language: value === "auto" ? null : value 
              })
            }
          >
            <BrutalSelectTrigger>
              <BrutalSelectValue placeholder="Automatica (segue interfaccia)" />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              <BrutalSelectItem value="auto">
                🌐 Automatica (attualmente: {i18n.language.toUpperCase()})
              </BrutalSelectItem>
              <BrutalSelectItem value="it">🇮🇹 Italiano</BrutalSelectItem>
              <BrutalSelectItem value="en">🇬🇧 English</BrutalSelectItem>
              <BrutalSelectItem value="es">🇪🇸 Español</BrutalSelectItem>
            </BrutalSelectContent>
          </BrutalSelect>
        </div>

        <div className="space-y-3">
          <PreferenceToggle
            label={t("preferences.newsletter")}
            description={t("preferences.newsletterDesc")}
            checked={preferences.email_newsletter}
            onChange={(v) =>
              setPreferences({ ...preferences, email_newsletter: v })
            }
          />

          <PreferenceToggle
            label={t("preferences.productUpdates")}
            description={t("preferences.productUpdatesDesc")}
            checked={preferences.email_product_updates}
            onChange={(v) =>
              setPreferences({ ...preferences, email_product_updates: v })
            }
          />

          <PreferenceToggle
            label={t("preferences.eventInvites")}
            description={t("preferences.eventInvitesDesc")}
            checked={preferences.email_event_invites}
            onChange={(v) =>
              setPreferences({ ...preferences, email_event_invites: v })
            }
          />

          <PreferenceToggle
            label={t("preferences.reviewNotifications")}
            description={t("preferences.reviewNotificationsDesc")}
            checked={preferences.email_review_notifications}
            onChange={(v) =>
              setPreferences({ ...preferences, email_review_notifications: v })
            }
          />

          <PreferenceToggle
            label={t("preferences.affiliateUpdates")}
            description={t("preferences.affiliateUpdatesDesc")}
            checked={preferences.email_affiliate_updates}
            onChange={(v) =>
              setPreferences({ ...preferences, email_affiliate_updates: v })
            }
          />
        </div>
      </BrutalCard>

      <BrutalCard className="mb-6">
        <h2 className="font-heading text-xl font-bold mb-4">
          {t("preferences.inAppNotifications")}
        </h2>

        <div className="space-y-3">
          <PreferenceToggle
            label={t("preferences.newReviews")}
            checked={preferences.notify_new_reviews}
            onChange={(v) =>
              setPreferences({ ...preferences, notify_new_reviews: v })
            }
          />

          <PreferenceToggle
            label={t("preferences.eventReminders")}
            checked={preferences.notify_event_reminders}
            onChange={(v) =>
              setPreferences({ ...preferences, notify_event_reminders: v })
            }
          />

          <PreferenceToggle
            label={t("preferences.subscriptionChanges")}
            checked={preferences.notify_subscription_changes}
            onChange={(v) =>
              setPreferences({ ...preferences, notify_subscription_changes: v })
            }
          />

          <PreferenceToggle
            label={t("preferences.payoutAvailable")}
            checked={preferences.notify_payout_available}
            onChange={(v) =>
              setPreferences({ ...preferences, notify_payout_available: v })
            }
          />

          <PreferenceToggle
            label={t("preferences.profileViews")}
            checked={preferences.notify_profile_views}
            onChange={(v) =>
              setPreferences({ ...preferences, notify_profile_views: v })
            }
          />
        </div>
      </BrutalCard>

      <BrutalCard className="mb-6">
        <h2 className="font-heading text-xl font-bold mb-4">
          {t("preferences.digestFrequency")}
        </h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="mb-2 block font-bold">
              {t("preferences.frequency")}
            </Label>
            <select
              className="w-full border-[3px] border-dark p-3 font-body text-base bg-card"
              value={preferences.digest_frequency}
              onChange={(e) =>
                setPreferences({ ...preferences, digest_frequency: e.target.value })
              }
            >
              <option value="never">{t("preferences.never")}</option>
              <option value="daily">{t("preferences.daily")}</option>
              <option value="weekly">{t("preferences.weekly")}</option>
              <option value="monthly">{t("preferences.monthly")}</option>
            </select>
          </div>

          <div>
            <Label className="mb-2 block font-bold">
              {t("preferences.preferredTime")}
            </Label>
            <BrutalInput
              type="time"
              value={preferences.preferred_time}
              onChange={(e) =>
                setPreferences({ ...preferences, preferred_time: e.target.value })
              }
            />
          </div>
        </div>
      </BrutalCard>

              <div className="flex justify-between items-center">
                <div className="flex gap-3">
                  <BrutalButton
                    variant="outline"
                    onClick={() => navigate("/dashboard")}
                    disabled={saving}
                  >
                    {t("common.cancel")}
                  </BrutalButton>
                  <BrutalButton
                    variant="outline"
                    onClick={() => setPreferences(defaultPreferences)}
                    disabled={saving}
                  >
                    {t("preferences.restoreDefaults")}
                  </BrutalButton>
                </div>
                
                <BrutalButton 
                  onClick={savePreferences}
                  disabled={saving}
                >
                  {saving ? t("common.saving") : t("preferences.savePreferences")}
                </BrutalButton>
              </div>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="space-y-6">
              <BrutalCard>
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="w-6 h-6 text-primary" />
                  <h2 className="font-heading text-xl font-bold">
                    {t("preferences.security.emailSection")}
                  </h2>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {t("preferences.security.currentEmail")}: <strong>{user?.email}</strong>
                </p>
                <BrutalButton onClick={() => setShowEmailDialog(true)}>
                  {t("preferences.security.changeEmail")}
                </BrutalButton>
              </BrutalCard>

              <BrutalCard>
                <div className="flex items-center gap-3 mb-4">
                  <Lock className="w-6 h-6 text-primary" />
                  <h2 className="font-heading text-xl font-bold">
                    {t("preferences.security.passwordSection")}
                  </h2>
                </div>
                <BrutalButton onClick={() => setShowPasswordDialog(true)}>
                  {t("preferences.security.changePassword")}
                </BrutalButton>
              </BrutalCard>

              <BrutalCard>
                <div className="flex items-center gap-3 mb-4">
                  <LogOut className="w-6 h-6 text-primary" />
                  <h2 className="font-heading text-xl font-bold">
                    {t("preferences.security.sessionsSection")}
                  </h2>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {t("preferences.security.currentSession")}
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <BrutalButton variant="outline">
                      {t("preferences.security.signOutAllDevices")}
                    </BrutalButton>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("preferences.security.signOutAllConfirm")}</AlertDialogTitle>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction onClick={handleSignOutAll}>
                        {t("common.submit")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </BrutalCard>
            </TabsContent>

            {/* Danger Zone Tab */}
            <TabsContent value="danger" className="space-y-6">
              <Alert variant="destructive" className="border-[3px]">
                <AlertTriangle className="h-5 w-5" />
                <AlertDescription className="font-bold">
                  {t("preferences.dangerZone.warning")}
                </AlertDescription>
              </Alert>

              <BrutalCard>
                <div className="flex items-center gap-3 mb-4">
                  <Trash2 className="w-6 h-6 text-destructive" />
                  <h2 className="font-heading text-xl font-bold text-destructive">
                    {t("preferences.dangerZone.deleteAccount")}
                  </h2>
                </div>

                {checkingSubscription ? (
                  <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
                ) : hasActiveSubscription ? (
                  <>
                    <Alert variant="destructive" className="mb-4 border-[3px]">
                      <AlertTriangle className="h-5 w-5" />
                      <AlertDescription>
                        <strong>{t("preferences.dangerZone.deleteAccountBlocked")}</strong>
                        <p className="mt-2 text-sm">
                          {t("preferences.dangerZone.deleteAccountBlockedDesc")}
                        </p>
                      </AlertDescription>
                    </Alert>
                    <div className="flex gap-3">
                      <BrutalButton onClick={() => navigate("/dashboard/subscription")}>
                        {t("preferences.dangerZone.goToSubscription")}
                      </BrutalButton>
                      <BrutalButton 
                        disabled 
                        variant="outline"
                        className="opacity-50 cursor-not-allowed"
                      >
                        🔒 {t("preferences.dangerZone.deleteAccountButtonDisabled")}
                      </BrutalButton>
                    </div>
                  </>
                ) : (
                  <>
                    <Alert variant="destructive" className="mb-4 border-[3px]">
                      <AlertTriangle className="h-5 w-5" />
                      <AlertDescription>
                        <strong>{t("preferences.dangerZone.deleteAccountWarning")}</strong>
                        <ul className="mt-2 space-y-1 text-sm list-disc list-inside">
                          <li>{t("preferences.dangerZone.deleteAccountDesc")}</li>
                        </ul>
                      </AlertDescription>
                    </Alert>
                    <BrutalButton 
                      onClick={() => setShowDeleteDialog(true)}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      🗑️ {t("preferences.dangerZone.deleteAccountButton")}
                    </BrutalButton>
                  </>
                )}
              </BrutalCard>
            </TabsContent>
          </Tabs>

          {/* Email Change Dialog */}
          <AlertDialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("preferences.security.changeEmail")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("preferences.security.emailVerificationSent")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-4 py-4">
                <BrutalInput
                  type="email"
                  placeholder={t("preferences.security.newEmail")}
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
                <BrutalInput
                  type="password"
                  placeholder={t("preferences.security.passwordRequired")}
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={handleChangeEmail}>
                  {t("common.save")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Password Change Dialog */}
          <AlertDialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("preferences.security.changePassword")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("preferences.security.passwordRequirements")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-4 py-4">
                <BrutalInput
                  type="password"
                  placeholder={t("preferences.security.currentPassword")}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <BrutalInput
                  type="password"
                  placeholder={t("preferences.security.newPassword")}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <BrutalInput
                  type="password"
                  placeholder={t("preferences.security.confirmNewPassword")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={handleChangePassword}>
                  {t("common.save")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Delete Account Dialog */}
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-destructive">
                  {t("preferences.dangerZone.deleteAccount")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t("preferences.dangerZone.deleteAccountWarning")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="confirm-delete" 
                    checked={deleteConfirmed}
                    onCheckedChange={(checked) => setDeleteConfirmed(checked as boolean)}
                  />
                  <label
                    htmlFor="confirm-delete"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {t("preferences.dangerZone.confirmCheckbox")}
                  </label>
                </div>
                <BrutalInput
                  type="password"
                  placeholder={t("preferences.dangerZone.passwordRequired")}
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteAccount}
                  disabled={!deleteConfirmed || !deletePassword || deleting}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  {deleting ? t("common.loading") : t("preferences.dangerZone.deleteAccountButton")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <Footer />
    </>
  );
}
